import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, CircularProgress, IconButton } from '@mui/material';
import { Add, Logout, CheckCircle, Schedule } from '@mui/icons-material';
import { supabase } from '../App';
import AppLogo from '../components/AppLogo';

const HomePage = () => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadApps();
  }, []);

  const loadApps = async () => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/');
        return;
      }
      setUser(user);
      console.log("Current user:", user);

      // Find profile by user_id (Supabase Auth UUID)
      let { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
      }

      if (!profiles) {
        // Profile will be created on first API call
        console.log('Profile not found, will be created on first action');
        setLoading(false);
        return;
      }

      // Get user's apps
      const { data: appsData, error } = await supabase
        .from('apps')
        .select('*')
        .eq('created_by', profiles.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching apps:', error);
        throw error;
      }

      console.log('Loaded apps:', appsData);
      setApps(appsData || []);
    } catch (error) {
      console.error('Error loading apps:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewProject = () => {
    navigate('/chat');
  };

  const handleAppClick = (appId) => {
    navigate(`/project/${appId}`);
  };

  // Split apps into published and private
  const publishedApps = apps.filter(app => app.is_published === true);
  const privateApps = apps.filter(app => app.is_published !== true);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 100%)',
      pb: 10,
      pt: 3,
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        px: 3,
        mb: 3,
        maxWidth: '1400px',
        mx: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <AppLogo />
        <IconButton onClick={handleLogout} sx={{
          color: '#9ca3af',
          '&:hover': { color: '#fafafa' }
        }}>
          <Logout />
        </IconButton>
      </Box>

      {/* Apps List */}
      <Box sx={{
        px: 2,
        maxWidth: '1400px',
        mx: 'auto',
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)'
        },
        gap: 2
      }}>
        {apps.length === 0 ? (
          <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 8 }}>
            <Typography sx={{ color: '#6b7280', fontSize: '16px' }}>
              No projects yet. Create your first one!
            </Typography>
          </Box>
        ) : (
          apps.map((app) => {
            const isDeployed = app.status === 'deployed' && app.code_url;
            const isPublished = app.is_published === true;
            return (
              <Card
                key={app.id}
                onClick={() => handleAppClick(app.id)}
                sx={{
                  background: 'rgba(255,255,255,0.03)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 3,
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(99, 102, 241, 0.5)',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 40px rgba(99, 102, 241, 0.15)'
                  }
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  {/* Status Badge */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.25,
                      borderRadius: '4px',
                      background: isPublished
                        ? 'rgba(16, 185, 129, 0.2)'
                        : isDeployed
                          ? 'rgba(99, 102, 241, 0.2)'
                          : 'rgba(245, 158, 11, 0.2)'
                    }}>
                      {isPublished ? (
                        <CheckCircle sx={{ fontSize: 14, color: '#10b981' }} />
                      ) : isDeployed ? (
                        <CheckCircle sx={{ fontSize: 14, color: '#6366f1' }} />
                      ) : (
                        <Schedule sx={{ fontSize: 14, color: '#f59e0b' }} />
                      )}
                      <Typography sx={{
                        fontSize: '11px',
                        fontWeight: 500,
                        color: isPublished ? '#10b981' : isDeployed ? '#a5b4fc' : '#f59e0b'
                      }}>
                        {isPublished ? 'Published' : isDeployed ? 'Ready' : 'In Progress'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* App Name */}
                  <Typography variant="h6" sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    mb: 1,
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {app.app_name || app.project_name || "Untitled"}
                  </Typography>

                  {/* Date */}
                  <Typography sx={{
                    color: '#6b7280',
                    fontSize: '12px'
                  }}>
                    {new Date(app.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </Typography>
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>

      {/* New Project Button */}
      <Box sx={{
        position: 'fixed',
        bottom: 32,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '90%',
        maxWidth: '400px'
      }}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleNewProject}
          startIcon={<Add />}
          sx={{
            py: 1.5,
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: 'white',
            fontSize: '15px',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              boxShadow: '0 12px 32px rgba(99, 102, 241, 0.4)',
            }
          }}
        >
          I have a new idea
        </Button>
      </Box>
    </Box>
  );
};

export default HomePage;
